/**
 * Shared React NodeView for the 5 named panels (info / note / success /
 * warning / error). Each per-kind extension passes its `NamedPanelSpec`
 * in via a closure (`buildNamedPanelView(spec)`); the view component
 * itself only needs the spec to render the right default icon + bg
 * (which the user can override via the in-panel picker).
 *
 * 2026-05-29 unification: named panels now accept `color` + `icon`
 * attrs that the user can change via the `<PanelPicker>` (was
 * custom-only before). Default visuals come from the kind's
 * `defaultColor` + `defaultIcon` in `NAMED_PANELS`.
 */

import { NodeViewContent, NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { useTranslation } from 'react-i18next'

import { PanelPicker } from './PanelPicker'
import {
  type CustomPanelColor,
  type CustomPanelIcon,
  type NamedPanelSpec,
  isCustomPanelColor,
  isCustomPanelIcon,
  resolveCustomPanelColorClasses,
  resolveCustomPanelIcon,
} from './panel-kinds'

/**
 * Build a NodeView component bound to a specific panel spec. The
 * caller passes the resulting component to `buildPanelExtension({ view })`.
 */
export function buildNamedPanelView(spec: NamedPanelSpec) {
  function NamedPanelView({ node, editor, getPos }: ReactNodeViewProps<HTMLElement>) {
    const { t } = useTranslation('wiki')

    // Resolve effective color + icon: instance attr if set, kind
    // default otherwise. The picker writes attrs on change so the
    // NodeView always reads the most recent visual the user picked.
    const rawColor = node.attrs.color as string | null | undefined
    const rawIcon = node.attrs.icon as string | null | undefined
    const colorSlug: CustomPanelColor = isCustomPanelColor(rawColor) ? rawColor : spec.defaultColor
    const iconSlug: CustomPanelIcon = isCustomPanelIcon(rawIcon) ? rawIcon : spec.defaultIcon
    const { panelBgClass, iconDiskClass } = resolveCustomPanelColorClasses(colorSlug)
    const Icon = resolveCustomPanelIcon(iconSlug)

    // `wiki.panel.<kind>.{ariaLabel,label}` keys are baked into
    // `en/wiki.json` already (Wave 1 added them).
    const ariaLabel = t(`panel.${spec.kind}.ariaLabel`)
    const forcedLabel = t(`panel.${spec.kind}.label`)

    return (
      <NodeViewWrapper
        as="div"
        data-block-type={spec.dataBlockType}
        data-color={colorSlug}
        data-icon={iconSlug}
        // biome-ignore lint/a11y/useSemanticElements: NodeViewWrapper renders a div with ProseMirror state attached; a real <section> would change PM's content model around this block. role="region" + aria-label gives the right ARIA shape.
        role="region"
        aria-label={ariaLabel}
        // Outer wrapper: `relative` so the floating picker below can
        // anchor against it. No tinted bg or rounded corners here —
        // those live on the inner chrome div so the picker's float
        // doesn't extend the panel's tint.
        className="group/panel relative my-4"
      >
        <span className="forced-color-label sr-only" aria-hidden="true">
          {forcedLabel}
        </span>

        {/* Inner chrome — owns the tinted background, rounded corners,
            padding, and the actual flex row with icon + body. Keeps
            the tint scope tight so the picker below floats OUTSIDE
            the panel's visual surface. */}
        <div className={`rounded-lg p-4 ${panelBgClass}`}>
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full ${iconDiskClass}`}
            >
              <Icon size={14} strokeWidth={2.5} className="text-white" aria-hidden="true" />
            </span>
            <NodeViewContent className="prose dark:prose-invert max-w-none flex-1 text-fg leading-6 prose-p:m-0 prose-p:leading-6" />
          </div>
        </div>

        {/* In-panel color + icon picker + kebab (Delete). Visible only
            when this specific panel contains the editor selection. The
            picker is shared with customPanel — `<PanelPicker>` reads
            the node's `color` + `icon` attrs and writes back on
            change. Delete option in the kebab removes the whole node. */}
        <PanelPicker
          editor={editor}
          getPos={getPos ?? null}
          nodeName={spec.nodeName}
          nodeSize={node.nodeSize}
          currentColor={colorSlug}
          currentIcon={iconSlug}
        />
      </NodeViewWrapper>
    )
  }

  // displayName helps in React DevTools + Vitest output.
  NamedPanelView.displayName = `${spec.nodeName.charAt(0).toUpperCase()}${spec.nodeName.slice(1)}View`
  return NamedPanelView
}
